import React from "react";

import { InfoSmIcon, Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import PageMeta from "Components/PageMeta";
import { Link, IconControl } from "ds/Button";
import * as Dialog from "ds/Dialog";
import Loading from "ds/PageLoading";
import * as Table from "ds/Table";
import { Tooltip } from "ds/Tooltip";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import {
  gitProjectSelector,
  canManageProjectVariablesSelector
} from "Reducers/project/project";
import { useLoadVariables } from "Reducers/project/settings/variable/hooks/useLoadVariables";
import Empty from "src/organization/pages/project/settings/variables/components/Empty/Empty";
import VariableLine from "src/organization/pages/project/settings/variables/components/VariableLine/VariableLine";

import * as S from "./ProjectVariables.styles";

import type { ProjectLevelVariable } from "@packages/client";

const ProjectVariables = () => {
  const { organizationId, projectId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
  }>();
  const intl = useIntl();
  const navigate = useNavigate();

  const [sort, setSort] = Table.useSort<{
    name: string;
    value: string;
  }>({
    key: "name",
    direction: "asc"
  });

  const sortVariables = (variables: ProjectLevelVariable[]) => {
    return [...variables].sort((a, b) => {
      if (sort.key === "name") {
        if (a.name < b.name) {
          return sort.direction === "asc" ? -1 : 1;
        }

        if (a.name > b.name) {
          return sort.direction === "asc" ? 1 : -1;
        }
        return 0;
      }

      if (a.value < b.value) {
        return sort.direction === "asc" ? -1 : 1;
      }

      if (a.value > b.value) {
        return sort.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  const project = useSelectorWithUrlParams(gitProjectSelector);
  const canManageProjectVariables = useSelectorWithUrlParams(
    canManageProjectVariablesSelector
  );
  const { variables, loading } = useLoadVariables({
    organizationId,
    projectId
  });

  function addVariable() {
    navigate(`/${organizationId}/${projectId}/-/settings/variables/-/new`);
  }

  const createBtn = (
    <S.AddButton
      analyticId="settings.variables.add.button"
      id="add-new-variable"
      onClick={addVariable}
      disabled={!canManageProjectVariables}
    >
      {intl.formatMessage({
        id: "settings.variables.add.button"
      })}
    </S.AddButton>
  );

  const createVariableEl = canManageProjectVariables ? (
    createBtn
  ) : (
    <Tooltip
      tooltip={intl.formatMessage({
        id: "project.variable.access"
      })}
      analyticId="project.variable.access"
    >
      {createBtn}
    </Tooltip>
  );

  return (
    <S.Wrapper>
      <PageMeta title={`Variables | ${project?.title}`} />
      <S.HeaderWrapper>
        <S.Title tag="h2" id="settings-heading">
          {intl.formatMessage({
            id: "project_variables",
            defaultMessage: "variables"
          })}{" "}
          {variables?.length > 0 && (
            <S.VariablesCount>({variables?.length})</S.VariablesCount>
          )}
        </S.Title>
        {variables?.length > 0 && createVariableEl}
      </S.HeaderWrapper>

      <S.PageDescription>
        <S.Details size="small">
          {intl.formatMessage({ id: "settings.project.variables.description" })}
        </S.Details>
        <S.NoteWrapper>
          <Dialog.Root>
            <Dialog.Trigger analyticId="settings.variables.details">
              <IconControl aria-label="info-trigger-var-details">
                <InfoSmIcon />
              </IconControl>
            </Dialog.Trigger>
            <S.Content>
              <Typography.BodyText size="small" weight="semiBold">
                {intl.formatMessage({
                  id: "settings.variables.variables"
                })}
              </Typography.BodyText>
              <Typography.BodyText size="small">
                {intl.formatMessage(
                  {
                    id: "settings.variables.details"
                  },
                  {
                    code: txt => <S.Code variant="inline">{txt}</S.Code>,
                    br: <br />,
                    appEnvironmentVariableName: intl.formatMessage({
                      id: "app_environment_variable_name"
                    })
                  }
                )}
              </Typography.BodyText>
            </S.Content>
          </Dialog.Root>
          <div>
            {intl.formatMessage<React.ReactNode>(
              { id: "settings.project.variables.note" },
              {
                link: txt => (
                  <Link
                    href={intl.formatMessage({
                      id: "links.documentation.app.variables"
                    })}
                  >
                    {txt}
                  </Link>
                )
              }
            )}
          </div>
        </S.NoteWrapper>
      </S.PageDescription>
      <section aria-labelledby="settings-heading">
        {variables?.length === 0 && !loading && (
          <Empty
            onClick={addVariable}
            disabled={!canManageProjectVariables}
            tooltipId="project.variable.access"
          />
        )}
        {loading ? (
          <Loading />
        ) : (
          <>
            {variables?.length > 0 && (
              <Table.Table>
                <S.TableThead>
                  <Table.Th
                    width="24%"
                    onSortChanged={() => setSort("name")}
                    isSortActive={sort.key === "name"}
                    sortDirection={sort.direction}
                  >
                    {intl.formatMessage({
                      id: "settings.project.variables.sort.name"
                    })}
                  </Table.Th>
                  <Table.Th
                    width="24%"
                    onSortChanged={() => setSort("value")}
                    isSortActive={sort.key === "value"}
                    sortDirection={sort.direction}
                  >
                    {intl.formatMessage({
                      id: "settings.project.variables.sort.value"
                    })}
                  </Table.Th>
                  <Table.Th width="24%">
                    {intl.formatMessage({
                      id: "settings.project.variables.sort.type"
                    })}
                  </Table.Th>
                  <Table.Th width="24%">&nbsp;</Table.Th>
                </S.TableThead>
              </Table.Table>
            )}
            {sortVariables(variables).map(variable => (
              <VariableLine key={`var-${variable.id}`} variable={variable} />
            ))}
          </>
        )}
      </section>
    </S.Wrapper>
  );
};

export default ProjectVariables;
